// client/src/lib/currencyRateApi.ts
// API client for currency rate management

import { get, postJson, deleteRequest } from './apiClient';

export interface CurrencyRate {
  _id: string;
  fromCurrency: string;
  toCurrency: string;
  /** null when no TT$/US$ rate is set (D102.7): every conversion is refused until one is. */
  rate: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  setBy: {
    _id: string;
    name?: string;
    email: string;
  };
  setByName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRatePayload {
  fromCurrency?: string;
  toCurrency?: string;
  rate: number;
  notes?: string;
  effectiveFrom?: string;
}

/**
 * Get the active currency rate
 */
export async function getActiveCurrencyRate(
  from: string = 'USD',
  to: string = 'TTD'
): Promise<CurrencyRate> {
  const response = await get<{ success: boolean; data: CurrencyRate }>(`/currency-rates/active?from=${from}&to=${to}`);
  return response.data;
}

/**
 * Get currency rate for a specific date
 */
export async function getCurrencyRateForDate(
  date: string,
  from: string = 'USD',
  to: string = 'TTD'
): Promise<CurrencyRate> {
  const response = await get<{ success: boolean; data: CurrencyRate }>(`/currency-rates/for-date?date=${date}&from=${from}&to=${to}`);
  return response.data;
}

/**
 * Get currency rate history
 */
export async function getCurrencyRateHistory(
  from: string = 'USD',
  to: string = 'TTD',
  limit: number = 50
): Promise<CurrencyRate[]> {
  const response = await get<{ success: boolean; data: CurrencyRate[] }>(`/currency-rates/history?from=${from}&to=${to}&limit=${limit}`);
  return response.data;
}

/**
 * Create a new currency rate
 */
export async function createCurrencyRate(payload: CreateRatePayload): Promise<CurrencyRate> {
  const response = await postJson<{ success: boolean; message: string; data: CurrencyRate }>('/currency-rates', payload);
  return response.data;
}

/**
 * Convert currency amount
 */
export async function convertCurrency(
  amount: number,
  from: string = 'USD',
  to: string = 'TTD',
  date?: string
): Promise<{
  originalAmount: number;
  fromCurrency: string;
  toCurrency: string;
  convertedAmount: number;
  date: string;
  timestamp: string;
}> {
  const response = await postJson<{
    success: boolean;
    data: {
      originalAmount: number;
      fromCurrency: string;
      toCurrency: string;
      convertedAmount: number;
      date: string;
      timestamp: string;
    };
  }>('/currency-rates/convert', {
    amount,
    from,
    to,
    date,
  });
  return response.data;
}

/**
 * Deactivate a currency rate
 */
export async function deactivateCurrencyRate(rateId: string): Promise<void> {
  await deleteRequest<{ success: boolean; message: string }>(`/currency-rates/${rateId}`);
}

